/**
 * A passport (Spec 018): the short, current account a newcomer — person or
 * agent — reads before working. One component for the business passport and a
 * project's: the text, how fresh it is, an editor that starts from the
 * template, and the history of who changed it and on what grounds.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BadgeCheck, History, PencilLine } from "lucide-react";

import { VersionConflictError } from "@/api/client";
import type { Freshness, PassportRevisionOut } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { TextArea, TextField } from "@/components/ui/Field";
import { Section } from "@/components/ui/Section";
import { Markdown } from "@/lib/markdown";

/** «Проверено 26.09.2026, Костя», or why it cannot be trusted as is. */
export function freshnessLabel(
  freshness: Freshness | undefined,
  checkedAt: string | null | undefined,
  checkedBy: string | null | undefined,
): string {
  if (!freshness || freshness === "never" || !checkedAt) return "Ещё никто не проверял";
  const when = new Date(checkedAt).toLocaleDateString("ru-RU");
  const who = checkedBy ? `, ${checkedBy}` : "";
  if (freshness === "changed") return `Изменён после проверки ${when}${who}`;
  return freshness === "stale" ? `Давно не проверяли: ${when}${who}` : `Проверено ${when}${who}`;
}

export interface PassportCardProps {
  title: string;
  body: string | null;
  /** The version the page shows; an edit is saved against the one it began from. */
  version: number;
  /** Where the text sits in the record a 409 returns as `current`. */
  bodyKey: "body_md" | "description_md";
  template: string;
  freshness: Freshness | undefined;
  checkedAt: string | null | undefined;
  checkedBy: string | null | undefined;
  historyKey: unknown[];
  loadHistory: () => Promise<PassportRevisionOut[]>;
  onSave: (body: string, source: string | null, version: number) => Promise<void>;
  onCheck: () => Promise<void>;
  /** Re-read the passport, e.g. after it changed under a «Проверено». */
  onRefresh: () => void;
}

export function PassportCard(props: PassportCardProps) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [source, setSource] = useState("");
  // The version this draft was written against. Taking the page's current one
  // at save time would overwrite a change made while the editor was open.
  const [base, setBase] = useState(props.version);
  // The passport text the draft began from: a conflict matters only if it changed.
  const [startBody, setStartBody] = useState("");
  const [conflict, setConflict] = useState<VersionConflictError | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [showHistory, setShowHistory] = useState(false);
  const history = useQuery({
    queryKey: props.historyKey,
    queryFn: props.loadHistory,
    enabled: showHistory,
  });
  const empty = !(props.body ?? "").trim();
  const theirs = conflict ? String(conflict.current[props.bodyKey] ?? "") : "";

  const save = async (version: number): Promise<void> => {
    const draft = text.trim();
    if (draft === startBody.trim()) {
      setEditing(false); // nothing changed, so nothing to write or to record
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await props.onSave(draft, source.trim() || null, version);
      setConflict(null);
      setEditing(false);
    } catch (e) {
      if (!(e instanceof VersionConflictError)) {
        setError(e instanceof Error ? e.message : "Не удалось сохранить");
        return;
      }
      props.onRefresh();
      const now = String(e.current[props.bodyKey] ?? "");
      if (now === startBody && e.actual !== version) {
        // Only the version moved (a check, a rename): the text is the one we
        // began from, so the draft goes on top of it without a question.
        setBase(e.actual);
        await save(e.actual);
        return;
      }
      setConflict(e);
    } finally {
      setBusy(false);
    }
  };

  const check = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await props.onCheck();
    } catch (e) {
      if (e instanceof VersionConflictError) {
        // «Проверено» confirms the text on screen; a newer one needs reading first.
        setError("Паспорт изменили, пока ты читал. Прочитай новый текст и отметь снова.");
        props.onRefresh();
      } else setError(e instanceof Error ? e.message : "Не удалось сохранить");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section title={props.title}>
      <Card className="space-y-2">
        <p className={props.freshness === "fresh" ? "text-meta text-hint" : "text-meta text-dstr"}>
          {freshnessLabel(props.freshness, props.checkedAt, props.checkedBy)}
        </p>
        {editing ? (
          <div className="space-y-2">
            <TextArea
              label="Текст паспорта"
              value={text}
              rows={14}
              maxLength={50000}
              onChange={(e) => setText(e.target.value)}
            />
            <TextField
              label="Откуда сведения — необязательно"
              value={source}
              maxLength={2000}
              placeholder="Например: PR #9, решение в обсуждении"
              onChange={(e) => setSource(e.target.value)}
            />
            {conflict && (
              <div role="alert" className="space-y-2 rounded-card border border-dstr p-3">
                <p className="text-meta text-dstr">
                  Пока ты редактировал, паспорт изменили. Ничего не записано, твой текст остался в
                  поле выше.
                </p>
                <p className="text-meta text-hint">Сейчас в паспорте:</p>
                <p className="whitespace-pre-wrap break-words text-meta text-text">
                  {theirs || "—"}
                </p>
                <div className="flex flex-wrap gap-1">
                  <Button
                    size="compact"
                    variant="ghost"
                    loading={busy}
                    onClick={() => {
                      // Seen both texts and chose theirs to lose: an informed overwrite.
                      const version = conflict.actual;
                      setBase(version);
                      setConflict(null);
                      void save(version);
                    }}
                  >
                    Сохранить мой текст поверх
                  </Button>
                  <Button
                    size="compact"
                    variant="ghost"
                    onClick={() => {
                      setText(theirs);
                      setStartBody(theirs);
                      setBase(conflict.actual);
                      setConflict(null);
                    }}
                  >
                    Начать с нового текста
                  </Button>
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <Button
                size="compact"
                loading={busy}
                disabled={!text.trim() || conflict !== null}
                onClick={() => void save(base)}
              >
                Сохранить
              </Button>
              <Button
                size="compact"
                variant="ghost"
                onClick={() => {
                  setConflict(null);
                  setEditing(false);
                }}
              >
                Отмена
              </Button>
            </div>
          </div>
        ) : empty ? (
          <p className="text-body text-hint">
            Паспорт ещё не заполнен. Начни с шаблона: агенты тоже смогут дополнять его
            подтверждёнными фактами.
          </p>
        ) : (
          <Markdown className="text-body" text={props.body} />
        )}
        {error && (
          <p role="alert" className="text-meta text-dstr">
            {error}
          </p>
        )}
        {!editing && (
          <div className="flex flex-wrap gap-1">
            <Button
              size="compact"
              variant="ghost"
              onClick={() => {
                setText(empty ? props.template : props.body ?? "");
                setStartBody(props.body ?? "");
                setBase(props.version);
                setConflict(null);
                setSource("");
                setError(undefined);
                setEditing(true);
              }}
            >
              <PencilLine className="h-4 w-4" aria-hidden="true" />
              {empty ? "Заполнить" : "Изменить"}
            </Button>
            {!empty && (
              <Button
                size="compact"
                variant="ghost"
                loading={busy}
                title="Отметить, что паспорт актуален"
                onClick={() => void check()}
              >
                <BadgeCheck className="h-4 w-4" aria-hidden="true" />
                Проверено
              </Button>
            )}
            <Button
              size="compact"
              variant="ghost"
              aria-expanded={showHistory}
              onClick={() => setShowHistory(!showHistory)}
            >
              <History className="h-4 w-4" aria-hidden="true" />
              История
            </Button>
          </div>
        )}
        {showHistory && history.isLoading && <p className="text-meta text-hint">Загружаем…</p>}
        {showHistory && history.isError && (
          <p role="alert" className="text-meta text-dstr">
            Не удалось загрузить историю.
          </p>
        )}
        {showHistory &&
          (history.data ?? []).map((r) => (
            <p key={r.id} className="border-t border-sep pt-2 text-meta text-hint">
              {new Date(r.created_at).toLocaleString("ru-RU")} · {r.author_name ?? "Участник"}
              {r.via_agent ? " через агента" : ""}
              {r.source_md ? ` · основание: ${r.source_md}` : ""}
            </p>
          ))}
        {showHistory && history.isSuccess && history.data.length === 0 && (
          <p className="text-meta text-hint">Правок пока нет.</p>
        )}
      </Card>
    </Section>
  );
}
